import { QueryClient, useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINTS } from '@/api/endpoints';
import { fetchClient } from '@/api/fetch-client';
import {
  GetAddendumSessionError,
  GetAddendumSessionRequest,
  GetAddendumSessionResponse,
  PostAddendumSessionError,
  PostAddendumSessionRequest,
  PostAddendumSessionResponse,
} from '@/api/model';

const KEY = {
  GET_ADDENDUM_SESSION: 'GET_ADDENDUM_SESSION',
  POST_ADDENDUM_SESSION: 'POST_ADDENDUM_SESSION',
} as const;

const request = {
  getAddendumSession: async (
    { payload: { studyId } }: GetAddendumSessionRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await fetchClient.FCT.get<GetAddendumSessionResponse>(
      `${ENDPOINTS.FCT.ADDENDUM_SESSION.STUDY}${studyId}`,
      { signal },
    );

    return data;
  },
  postAddendumSession: async ({ payload: { studyId } }: PostAddendumSessionRequest) => {
    const { data } = await fetchClient.FCT.post<PostAddendumSessionResponse>(
      ENDPOINTS.FCT.ADDENDUM_SESSION.BASE,
      { body: { studyId } },
    );

    return data;
  },
};

const query = {
  useGetAddendumSession: (
    { payload }: GetAddendumSessionRequest,
    config?: Partial<UseQueryOptions<GetAddendumSessionResponse, GetAddendumSessionError>>,
  ) =>
    useQuery<GetAddendumSessionResponse, GetAddendumSessionError>({
      enabled: !!payload.studyId,
      queryFn: ({ signal }) => request.getAddendumSession({ payload }, signal),
      queryKey: [KEY.GET_ADDENDUM_SESSION, payload.studyId],
      ...config,
    }),
};

const mutation = {
  usePostAddendumSession: (queryClient: QueryClient) =>
    useMutation<PostAddendumSessionResponse, PostAddendumSessionError, PostAddendumSessionRequest>({
      mutationFn: request.postAddendumSession,
      onSuccess: (data, { payload: { studyId } }) => {
        queryClient.setQueryData<GetAddendumSessionResponse>(
          [KEY.GET_ADDENDUM_SESSION, studyId],
          (prev) => ({
            activeSession: data,
            canEnterAddendum: prev?.canEnterAddendum ?? false,
            isCurrentUserSession: true,
            permissionReason: prev?.permissionReason ?? null,
          }),
        );
      },
    }),
};

export const addendumSession = {
  KEY,
  mutation,
  query,
  request,
};
